<script setup lang="ts">
import type { DialogName } from '@/stores/useDialogStore';

import { useRoute } from 'vue-router';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import RequestChip from '@/components/chips/RequestChip.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import TicketsDashboardSearchForm from '@/components/searchForms/TicketsDashboardSearchForm.vue';
import TicketsDashboardTable from '@/components/tables/TicketsDashboardTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useTicketsDashboardViewData from '@/composables/data/useTicketsDashboardViewData';
import useTicketsRouteQuery from '@/composables/routeQueries/useTicketsRouteQuery';
import { useDialogStore } from '@/stores/useDialogStore';
import { useTicketsDashboardViewStore } from '@/stores/useTicketsDashboardViewStore';
import { ticketsStatusItems } from '@/constants/fieldItems';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

const routeQuery = useTicketsRouteQuery({
  curPage: defCurPage,
  pageSize: defPageSize,
  // TODO by default none is all
  // status: ticketsStatusItems.map((item) => item.value), // all statuses
  search: '',
  isRfc: false,
  isUnread: true,
});

const ticketsDashboardViewStore = useTicketsDashboardViewStore();
ticketsDashboardViewStore.init(
  useTicketsDashboardViewData(routeQuery),
  routeQuery,
);

const route = useRoute();

const dialogStore = useDialogStore();

function openNewTicketDialog() {
  // if (!ticketsDashboardViewStore.data.detailedJob) return;
  // if (!ticketsDashboardViewStore.data.detailedCustomer) return;

  // TODO it seems ops_emails, channelsEmails should be writable
  const dialogName: DialogName = 'newTicket';
  dialogStore
    .open(dialogName, {
      // TODO
      // jobId: ticketsDashboardViewStore.data.detailedJob.rockhopper_id,
      // jobDate: ticketsDashboardViewStore.data.detailedJob.rockhopper_date,
      // // @ts-expect-error
      // opsEmails: ticketsDashboardViewStore.data.detailedCustomer.ops_emails,
      // // @ts-expect-error
      // channelsEmails: ticketsDashboardViewStore.data.channelsEmails,
    })
    .confirmed(async function () {
      // TODO
      // if (ticketsDashboardViewStore.data.refresh_) {
      //   await ticketsDashboardViewStore.data.refresh_();
      // }
    });
}
</script>

<template>
  <div :class="$style.ticketsDashboardView" data-testid="ticketsDashboardView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <TicketsDashboardSearchForm
          :initial-data="ticketsDashboardViewStore.initialData"
          :loading="ticketsDashboardViewStore.data.isFetchingJobTicketsData"
          :class="$style.form"
          @submit="ticketsDashboardViewStore.search"
        />
      </FormWrapper>
      <div
        v-if="
          routeQuery.status.value ||
          routeQuery.search.value ||
          routeQuery.isRfc.value ||
          routeQuery.isUnread.value
        "
        :class="$style.requests"
      >
        <div :class="$style.leftRequests">
          Filters applied
          <RequestChip param-title="search" :class="$style.requestChip">
            Search {{ routeQuery.search.value }}
          </RequestChip>
          <RequestChip
            v-if="routeQuery.isRfc.value"
            param-title="is_rfc"
            :class="$style.requestChip"
          >
            Requested for change only
          </RequestChip>
          <!-- TODO make a better way for default chips and booleans -->
          <RequestChip
            v-if="routeQuery.isUnread.value"
            param-title="is_unread"
            :param-value="(!routeQuery.isUnread.value)?.toString()"
            :is-value="routeQuery.isUnread.value"
            :class="$style.requestChip"
          >
            Unread only
          </RequestChip>
          <template
            v-for="(status, index) in routeQuery.status.value"
            :key="status"
          >
            <RequestChip
              param-title="status"
              :param-value="
                (routeQuery.status.value || [])
                  .filter((_status) => _status !== status)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              Ticket status
              {{
                ticketsStatusItems.find(
                  (ticketsStatusItem) => ticketsStatusItem.value === status,
                )?.title
              }}
            </RequestChip>
          </template>
        </div>
        <div class="text-right">
          <!-- TODO make a better way for default chips and booleans -->
          <KBtn
            variant="text"
            :to="{ name: route.name, query: { is_unread: false } }"
          >
            Clear all requests
          </KBtn>
        </div>
      </div>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <TicketsDashboardTable
          :data="ticketsDashboardViewStore.ticketsDashboardTableData"
          :loading="ticketsDashboardViewStore.data.isFetchingJobTicketsData"
        />
        <TablePageFooter
          v-if="ticketsDashboardViewStore.data.jobTicketsData?.results.length"
          :cur-page="ticketsDashboardViewStore.query.curPage"
          :page-size="ticketsDashboardViewStore.query.pageSize"
          :total="ticketsDashboardViewStore.data.jobTicketsData.count"
        >
          <ExportAsCsvIconBtn
            :disabled="
              ticketsDashboardViewStore.data.isFetchingJobTicketsData ||
              !ticketsDashboardViewStore.data.jobTicketsData?.results?.length
            "
            :loading="ticketsDashboardViewStore.isDownloadingCSV"
            @click="ticketsDashboardViewStore.downloadCSV"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
    <TableFooterBar>
      <KBtn
        size="lg"
        append-icon="mdi-plus"
        :loading="false"
        @click="openNewTicketDialog"
      >
        Create
      </KBtn>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.ticketsDashboardView {
}
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: variables.$tableWrapperWidth;
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
    max-width: variables.$tableWrapperWidth;
  }
  .requests {
    max-width: variables.$tableWrapperWidth;
    display: flex;
    align-items: start;
    justify-content: space-between;
    margin-bottom: 14px;
  }
    .leftRequests {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
      .requestChip:first-child {
        margin-left: 8px;
      }
      .requestChip {
        margin-right: 8px;
        margin-top: 6px;
        margin-bottom: 6px;
      }
</style>
